import {EMAIL_MAKER_COMPONENT_TYPES} from '@EmailMakerReact/email-maker-react.constants';
import {makeColorControl} from '../../controls/color-control/color-control';
import {textColorDescriptor} from '../../controls/color-control/color-control-descriptors';
import {makeConditionalControl} from '../../controls/conditional-control/conditional-control';
import {makeMobileSheetGroup} from '../../controls/mobile-sheet-group/mobile-sheet-group';
import {ConditionalGatingSource} from '../../controls/conditional-control/conditional-control.types';
import {CountdownDeadlineControl} from '../../controls/countdown-deadline-control/countdown-deadline-control';
import {CountdownLabelsControl} from '../../controls/countdown-labels-control/countdown-labels-control';
import {makeCtaButtonControl} from '../../controls/cta-button-control/cta-button-control';
import {copyPasteStylesDescriptor} from '../../controls/cta-button-control/cta-button-control-descriptors';
import {makeDropdownSelectorControl} from '../../controls/dropdown-selector-control/dropdown-selector-control';
import {makeFontSelectorControl} from '../../controls/font-selector-control/font-selector-control';
import {makeOptionListControl} from '../../controls/option-list-control/option-list-control';
import {horizontalAlignDescriptor} from '../../controls/option-list-control/option-list-control-descriptors';
import {makeSliderControl} from '../../controls/slider-control/slider-control';
import {borderRadiusDescriptor, fontSizeDescriptor, paddingDescriptor} from '../../controls/slider-control/slider-control-descriptors';
import {makeStyleHeader} from '../../controls/style-header/style-header';
import {makeToggleSwitchControl} from '../../controls/toggle-switch-control/toggle-switch-control';
import {makeUploadButtonControl} from '../../controls/upload-button-control/upload-button-control';
import {registerEmailMakerSector} from '../../right-panel-registry';
import {TileComplexity} from '@EmailMakerReact/lib/email-maker-tile-meta-library.types';
import type {EmailMakerControl, EmailMakerSectorProperty} from '../../right-panel-registry.types';
import {borderStyleControl, conditionalBorderColorControl, conditionalBorderWidthControl} from '../shared/border-controls';
import {bgColorControl} from '../shared/color-controls';
import {deleteControl, duplicateControl, lockControl} from '../shared/common-action-controls';
import {
  areLabelsShown,
  countdownBackgroundImageDescriptor,
  countdownWidthDescriptor,
  labelColorDescriptor,
  labelFontDescriptor,
  labelSizeDescriptor,
  numbersAlignmentDescriptor,
  numbersBoldDescriptor,
  separatorDescriptor,
  showDaysToggleDescriptor,
  showLabelsToggleDescriptor,
} from './countdown-sector.descriptors';

const numbersHeader: EmailMakerControl = makeStyleHeader('pmwjs_numbers');
const labelsHeader: EmailMakerControl = makeStyleHeader('pmwjs_labels');

const showDaysControl = makeToggleSwitchControl(showDaysToggleDescriptor);
const showLabelsControl = makeToggleSwitchControl(showLabelsToggleDescriptor);

const backgroundImageControl: EmailMakerControl = makeUploadButtonControl(countdownBackgroundImageDescriptor);
const borderRadiusControl = makeSliderControl(borderRadiusDescriptor);
const paddingControl = makeSliderControl(paddingDescriptor);
const widthControl = makeSliderControl(countdownWidthDescriptor);
const horizontalAlignControl = makeOptionListControl(horizontalAlignDescriptor);

const numbersAlignmentControl = makeOptionListControl(numbersAlignmentDescriptor);

const numbersFontControl = makeFontSelectorControl();
const numbersSizeControl = makeSliderControl(fontSizeDescriptor);
const numbersColorControl = makeColorControl(textColorDescriptor);
const numbersBoldControl = makeOptionListControl(numbersBoldDescriptor);
const separatorControl = makeDropdownSelectorControl(separatorDescriptor);

const labelFontControl = makeFontSelectorControl(labelFontDescriptor);
const labelSizeControl = makeSliderControl(labelSizeDescriptor);
const labelColorControl = makeColorControl(labelColorDescriptor);
const copyPasteStylesControl = makeCtaButtonControl(copyPasteStylesDescriptor);

function onlyWhenLabelsShown(control: EmailMakerControl): EmailMakerControl {
  return makeConditionalControl({control, predicate: areLabelsShown, gatingSource: ConditionalGatingSource.ATTRIBUTE, gatingKey: showLabelsToggleDescriptor.attribute});
}

const labelsTextControl = onlyWhenLabelsShown(CountdownLabelsControl);
const labelsOnlyFontControl = onlyWhenLabelsShown(labelFontControl);
const labelsOnlySizeControl = onlyWhenLabelsShown(labelSizeControl);
const labelsOnlyColorControl = onlyWhenLabelsShown(labelColorControl);

const deadlineSheet = makeMobileSheetGroup([CountdownDeadlineControl, showDaysControl]);
const borderSheet = makeMobileSheetGroup([borderStyleControl, conditionalBorderColorControl, conditionalBorderWidthControl]);
const numbersSheet = makeMobileSheetGroup([numbersFontControl, numbersSizeControl, numbersColorControl, numbersBoldControl, separatorControl]);
const labelsSheet = makeMobileSheetGroup([showLabelsControl, labelsTextControl, labelsOnlyFontControl, labelsOnlySizeControl, labelsOnlyColorControl]);

const COUNTDOWN_PROPERTIES: readonly EmailMakerSectorProperty[] = [
  {control: duplicateControl},
  {control: lockControl},
  {control: deleteControl},
  {control: CountdownDeadlineControl},
  {control: showDaysControl},
  {control: backgroundImageControl},
  {control: bgColorControl},
  {control: borderRadiusControl},
  {control: borderStyleControl},
  {control: conditionalBorderColorControl},
  {control: conditionalBorderWidthControl},
  {control: paddingControl},
  {control: widthControl},
  {control: numbersAlignmentControl},
  {control: horizontalAlignControl},
  {control: numbersHeader},
  {control: numbersFontControl},
  {control: numbersSizeControl},
  {control: numbersColorControl},
  {control: numbersBoldControl},
  {control: separatorControl},
  {control: labelsHeader},
  {control: showLabelsControl},
  {control: labelsTextControl},
  {control: labelsOnlyFontControl},
  {control: labelsOnlySizeControl},
  {control: labelsOnlyColorControl},
  {control: copyPasteStylesControl},
];

const COUNTDOWN_MOBILE_PROPERTIES: readonly EmailMakerSectorProperty[] = [
  {control: duplicateControl},
  {control: lockControl},
  {control: deleteControl},
  {control: deadlineSheet, tileMeta: {icon: 'icon-clock-o', labelKey: 'pmwjs_end_date', complexity: TileComplexity.COMPLEX}},
  {control: numbersSheet, tileMeta: {icon: 'icon-number', labelKey: 'pmwjs_numbers', complexity: TileComplexity.COMPLEX}},
  {control: labelsSheet, tileMeta: {icon: 'icon-tags', labelKey: 'pmwjs_labels', complexity: TileComplexity.COMPLEX}},
  {control: backgroundImageControl, tileMeta: {icon: 'icon-image', labelKey: 'pmwjs_background_image', complexity: TileComplexity.SIMPLE}},
  {control: bgColorControl, tileMeta: {icon: 'icon-fill', labelKey: 'pmwjs_background_color', complexity: TileComplexity.SIMPLE}},
  {control: borderRadiusControl, tileMeta: {icon: 'icon-corner', labelKey: 'pmwjs_roundness', complexity: TileComplexity.COMPLEX}},
  {control: borderSheet, tileMeta: {icon: 'icon-stroke', labelKey: 'pmwjs_border', complexity: TileComplexity.SIMPLE}},
  {control: paddingControl, tileMeta: {icon: 'icon-edge-spacing', labelKey: 'pmwjs_spacing', complexity: TileComplexity.COMPLEX}},
  {control: widthControl, tileMeta: {icon: 'icon-stretch-horizontal', labelKey: 'pmwjs_width', complexity: TileComplexity.SIMPLE}},
  {control: numbersAlignmentControl, tileMeta: {icon: 'icon-align-center', labelKey: 'pmwjs_alignment', complexity: TileComplexity.SIMPLE}},
  {control: horizontalAlignControl, tileMeta: {icon: 'icon-h-align-center', labelKey: 'pmwjs_horizontal_alignment', complexity: TileComplexity.SIMPLE}},
];

registerEmailMakerSector(EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_COUNTDOWN, {
  title: window.i18next.t('pmwjs_countdown'),
  properties: COUNTDOWN_PROPERTIES,
  mobileProperties: COUNTDOWN_MOBILE_PROPERTIES,
});
